import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Formik, Field, Form } from "formik";
import tw from "tailwind-styled-components";
import { MdClose, MdEdit } from "react-icons/md";
import {
    ModalHeader,
    ActionSection,
    SideCancelBtn,
    SubmitBtn,
    InputGroup,
    FieldWrappers,
    Label,
    Wrapper,
    ScrollableContent,
} from "../../Styles/InputStyles";
import SelectOptionModal from "../../SelectOptionModal";
import { toast } from "react-toastify";
import { getFoodCategoryLabel, getFoodUnitLabel } from "../../../helpers";
import { foodItemCategories, inventoryUnits } from "../../../Config";

const AddItem = ({
    handleClose,
    addItemMutate,
    addItemLoading = false,
}) => {
    const [height, setHeight] = useState(window.innerHeight);
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [isUnitModalOpen, setIsUnitModalOpen] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const initialValues = {
        categoryId: "",
        categoryName: "",
        name: "",
        unitType: "",
        unitLabel: "",
    };

    // Get category label by id
    const getCategoryLabel = useCallback((id) => {
        if (!id) return "Select Category";
        const category = foodItemCategories.find(c => c.value === id);
        return category ? category.label : "Select Category";
    }, []);

    // Get unit label by id
    const getUnitLabel = useCallback((id) => {
        if (!id) return "Select Unit";
        const unit = inventoryUnits.find(u => u.value === id);
        return unit ? unit.label : "Select Unit";
    }, []);

    const validate = (values) => {
        const errors = {};

        if (!values.categoryId) {
            errors.categoryId = "Category is required";
        }

        if (!values.name || values.name.trim() === "") {
            errors.name = "Item name is required";
        } else if (values.name.length < 2) {
            errors.name = "Item name must be at least 2 characters";
        } else if (values.name.length > 50) {
            errors.name = "Item name must be less than 50 characters";
        }

        if (!values.unitType) {
            errors.unitType = "Unit type is required";
        }

        return errors;
    };

    const handleSubmit = async (values, { resetForm, setSubmitting }) => {
        // Prepare payload
        const payload = {
            categoryId: parseInt(values.categoryId),
            name: values.name.trim(),
            unitType: parseInt(values.unitType),
        };

        // Call the mutation
        addItemMutate(payload);
    };

    const handleCategorySelect = (selectedValue, setFieldValue) => {
        if (selectedValue) {
            setFieldValue("categoryId", selectedValue);
            const category = foodItemCategories.find(c => c.value === selectedValue);
            if (category) {
                setFieldValue("categoryName", category.label);
            }
        } else {
            setFieldValue("categoryId", "");
            setFieldValue("categoryName", "");
        }
        setIsCategoryModalOpen(false);
    };

    const handleUnitSelect = (selectedValue, setFieldValue) => {
        if (selectedValue) {
            setFieldValue("unitType", selectedValue);
            const unit = inventoryUnits.find(u => u.value === selectedValue);
            if (unit) {
                setFieldValue("unitLabel", unit.label);
            }
        } else {
            setFieldValue("unitType", "");
            setFieldValue("unitLabel", "");
        }
        setIsUnitModalOpen(false);
    };

    return (
        <Container style={{ height }}>
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">Add Food Item</h1>
                <button onClick={handleClose} type="button">
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <div className="px-6 h-full flex flex-col justify-between">
                <Formik
                    initialValues={initialValues}
                    validate={validate}
                    onSubmit={handleSubmit}
                    validateOnChange={true}
                    validateOnBlur={true}
                    enableReinitialize={false}
                >
                    {({ values, setFieldValue, errors, touched, isSubmitting }) => (
                        <Form>
                            <ScrollableContent className="max-h-[calc(100vh-120px)]">
                                <Wrapper>
                                    {/* Category Selection */}
                                    <InputGroup>
                                        <Label>Category *</Label>
                                        <FieldWrappers
                                            onClick={() => setIsCategoryModalOpen(true)}
                                            className="cursor-pointer"
                                        >
                                            <Field
                                                type="text"
                                                name="categoryName"
                                                readOnly
                                                value={getCategoryLabel(values.categoryId)}
                                                className="w-full border-none outline-none bg-transparent cursor-pointer"
                                                placeholder="Select Category"
                                            />
                                            <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                                        </FieldWrappers>
                                        {errors.categoryId && touched.categoryId && (
                                            <ErrorText>{errors.categoryId}</ErrorText>
                                        )}
                                    </InputGroup>

                                    {/* Item Name */}
                                    <InputGroup>
                                        <Label htmlFor="name">Item Name *</Label>
                                        <FieldWrappers>
                                            <Field
                                                type="text"
                                                name="name"
                                                id="name"
                                                placeholder="Enter item name (e.g., Basmati Rice)"
                                                className="w-full"
                                            />
                                        </FieldWrappers>
                                        {errors.name && touched.name && (
                                            <ErrorText>{errors.name}</ErrorText>
                                        )}
                                    </InputGroup>

                                    {/* Unit Type Selection */}
                                    <InputGroup>
                                        <Label>Unit Type *</Label>
                                        <FieldWrappers
                                            onClick={() => setIsUnitModalOpen(true)}
                                            className="cursor-pointer"
                                        >
                                            <Field
                                                type="text"
                                                name="unitLabel"
                                                readOnly
                                                value={getUnitLabel(values.unitType)}
                                                className="w-full border-none outline-none bg-transparent cursor-pointer"
                                                placeholder="Select Unit Type"
                                            />
                                            <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                                        </FieldWrappers>
                                        {errors.unitType && touched.unitType && (
                                            <ErrorText>{errors.unitType}</ErrorText>
                                        )}
                                    </InputGroup>

                                </Wrapper>
                            </ScrollableContent>

                            <ActionSection>
                                <SideCancelBtn type="button" onClick={handleClose}>
                                    Cancel
                                </SideCancelBtn>
                                <SubmitBtn
                                    type="submit"
                                    disabled={isSubmitting || addItemLoading}
                                >
                                    {isSubmitting || addItemLoading ? "Submitting..." : "Submit"}
                                </SubmitBtn>
                            </ActionSection>

                            {/* Category Selection Modal */}
                            <SelectOptionModal
                                isOpen={isCategoryModalOpen}
                                onClose={() => setIsCategoryModalOpen(false)}
                                title="Select Category"
                                options={foodItemCategories}
                                selectedValue={values.categoryId}
                                onSelect={(value) => handleCategorySelect(value, setFieldValue)}
                                searchPlaceholder="Search categories..."
                            />

                            {/* Unit Type Selection Modal */}
                            <SelectOptionModal
                                isOpen={isUnitModalOpen}
                                onClose={() => setIsUnitModalOpen(false)}
                                title="Select Unit Type"
                                options={inventoryUnits}
                                selectedValue={values.unitType}
                                onSelect={(value) => handleUnitSelect(value, setFieldValue)}
                                searchPlaceholder="Search unit types..."
                            />
                        </Form>
                    )}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const ErrorText = tw.div`text-red-500 text-sm ml-0.5 mt-1`;
const InfoBox = tw.div`
    mt-4 p-4 
    bg-blue-50 
    border border-blue-200 
    rounded-lg
`;
const InfoTitle = tw.h4`
    text-sm font-medium text-blue-800 
    mb-1
`;
const InfoText = tw.p`
    text-sm text-blue-600
`;

export default AddItem;